import React, { useState, useEffect, useRef } from "react";
import { CustomDatePicker } from "./components/CustomDatePicker/CustomDatePicker";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { SearchMultiSelect } from "./components/SearchMultiSelect/SearchMultiSelect";
import { MultiValue } from "react-select";
import { useTranslation } from "react-i18next";
import { FilterState } from "../../../../interfaces/FilterState";
import { Tooltip } from "react-tooltip";
import { FaSortAlphaDown, FaSortAlphaUp } from "react-icons/fa";
import { useOnClickOutside } from "../../../../../../hooks/useOnClickOutside";
import { IListItem } from "../../../../interfaces/IDocDetails";
import { useDefaultFiltersContext } from "../../../../../../context/DefaultFiltersContext";

interface IFiltersProps {
  filtersData: {
    keywords: string[];
    tags: string[];
    country: string[];
    regulator: string[];
    classes: string[];
    tech_product: string[];
    tech_section_vat_act: string[];
    gazette_number: string[];
    judges: string[];
    status: string[];
    city: string[];
    subject_matter: string[];
    tax_alert: string[];
  };
  tags: string[];
  filters: FilterState;
  onFiltersChange: (updatedFilters: IFiltersProps["filters"]) => void;
  onClearSearch: () => void;
  search: string;
  items?: IListItem[];
}

const Filters: React.FC<IFiltersProps> = ({
  filtersData,
  tags,
  filters,
  onFiltersChange,
  onClearSearch,
  search,
  items,
}) => {
  const { t } = useTranslation();

  const readSettingsFromLS = () => {
    try {
      const raw = localStorage.getItem("settings");
      if (!raw) return { country: [], regulator: [], classes: [], filtersExpanded: false };
      const parsed = JSON.parse(raw);
      return {
        country: parsed.country || [],
        regulator: parsed.regulator || [],
        classes: parsed.classes || [],
        filtersExpanded: Boolean(parsed.filtersExpanded),
      };
    } catch {
      return { country: [], regulator: [], classes: [], filtersExpanded: false };
    }
  };

  const [settingsFilter, setSettingsFilter] = useState(readSettingsFromLS());

  const filtersRef = useRef<HTMLDivElement>(null);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isRegulatorOpen, setIsRegulatorOpen] = useState(false);
  const [isPublicationDateOpen, setIsPublicationDateOpen] = useState(false);
  const [isIssueDateOpen, setIsIssueDateOpen] = useState(false);
  const [isClassOpen, setIsClassOpen] = useState(false);
  const [isKeywordOpen, setIsKeywordOpen] = useState(false);
  const [isTechProductOpen, setIsTechProductOpen] = useState(false);
  const [isTechSectionOpen, setIsTechSectionOpen] = useState(false);
  const [isTaxAlertOpen, setIsTaxAlertOpen] = useState(false);
  const [isGazetteNumberOpen, setIsGazetteNumberOpen] = useState(false);
  const [isJudgesOpen, setIsJudgesOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [isSubjectMatterOpen, setIsSubjectMatterOpen] = useState(false);

  const [keywordValue, setKeywordValue] = useState<string[]>(filters.keywords);
  const [tagValue, setTagValue] = useState<string[]>(filters.tags);
  const [regulatorValue, setRegulatorValue] = useState<string[]>(
    filters.regulator,
  );
  const [countryValue, setCountryValue] = useState<string[]>(filters.country);
  const [classValue, setClassValue] = useState<string[]>(filters.classes);
  const [techProductValue, setTechProductValue] = useState<
    string[] | undefined
  >(filters.tech_product);
  const [techSectionValue, setTechSectionValue] = useState<
    string[] | undefined
  >(filters.tech_section_vat_act);
  const [gazetteNumberValue, setGazetteNumberValue] = useState<
    string[] | undefined
  >(filters.gazette_number);
  const [taxAlertValue, setTaxAlertValue] = useState<string[] | undefined>(
    filters.tax_alert,
  );
  const [judgesValue, setJudgesValue] = useState<string[] | undefined>(
    filters.judges,
  );
  const [statusValue, setStatusValue] = useState<string[] | undefined>(
    filters.status,
  );
  const [cityValue, setCityValue] = useState<string[] | undefined>(
    filters.city,
  );
  const [subjectMatterValue, setSubjectMatterValue] = useState<
    string[] | undefined
  >(filters.subject_matter);

  const [publicationDateValue, setPublicationDateValue] = useState<
    [Date | any, Date | any]
  >([filters.date_from, filters.date_to]);
  const [issueDateValue, setIssueDateValue] = useState<
    [Date | any, Date | any]
  >([filters.date_issue_from, filters.date_issue_to]);

  useEffect(() => {
    const handleSettingsUpdated = () => {
      setSettingsFilter(readSettingsFromLS());
    };
    window.addEventListener(
      "settings:updated",
      handleSettingsUpdated as EventListener,
    );
    return () =>
      window.removeEventListener(
        "settings:updated",
        handleSettingsUpdated as EventListener,
      );
  }, []);

  useEffect(() => {
    const next = Boolean(settingsFilter.filtersExpanded);
    setIsCountryOpen(next);
    setIsRegulatorOpen(next);
    setIsPublicationDateOpen(next);
    setIsIssueDateOpen(next);
    setIsClassOpen(next);
    setIsKeywordOpen(next);
    setIsTechProductOpen(next);
    setIsTechSectionOpen(next);
    setIsTaxAlertOpen(next);
    setIsGazetteNumberOpen(next);
    setIsJudgesOpen(next);
    setIsStatusOpen(next);
    setIsCityOpen(next);
    setIsSubjectMatterOpen(next);
  }, [settingsFilter.filtersExpanded]);

  const keywordsData = Array.from(
    new Set(filtersData?.keywords ? [...filtersData.keywords] : []),
  ).map((keyword) => ({
    value: keyword,
    label: keyword,
  }));
  const tagsData = Array.from(
    new Set(filtersData?.tags ? [...filtersData.tags, ...tags] : [...tags]),
  ).map((tag) => ({
    value: tag,
    label: tag,
  }));
  const techProductData = Array.from(
    new Set(filtersData?.tech_product ? [...filtersData?.tech_product] : []),
  ).map((techProduct) => ({
    value: techProduct,
    label: techProduct,
  }));
  const techSectionData = Array.from(
    new Set(
      filtersData?.tech_section_vat_act
        ? [...filtersData?.tech_section_vat_act]
        : [],
    ),
  ).map((techSection) => ({
    value: techSection,
    label: techSection,
  }));
  const taxAlertData = Array.from(
    new Set(filtersData?.tax_alert ? [...filtersData?.tax_alert] : []),
  ).map((taxAlert) => ({
    value: taxAlert,
    label: taxAlert,
  }));
  const gazetteNumberData = Array.from(
    new Set(
      filtersData?.gazette_number ? [...filtersData?.gazette_number] : [],
    ),
  ).map((gazetteNumber) => ({
    value: gazetteNumber,
    label: gazetteNumber,
  }));
  const judgesData = Array.from(
    new Set(filtersData?.judges ? [...filtersData.judges] : []),
  ).map((judge) => ({
    value: judge,
    label: judge,
  }));
  const statusData = Array.from(
    new Set(filtersData?.status ? [...filtersData.status] : []),
  ).map((status) => ({
    value: status,
    label: status,
  }));
  const cityData = Array.from(
    new Set(filtersData?.city ? [...filtersData.city] : []),
  ).map((city) => ({
    value: city,
    label: city,
  }));
  const subjectMatterData = Array.from(
    new Set(filtersData?.subject_matter ? [...filtersData.subject_matter] : []),
  ).map((subjectMatter) => ({
    value: subjectMatter,
    label: subjectMatter,
  }));

  const { defaultFilters } = useDefaultFiltersContext();

  // Debugging: log backend-provided filters and computed regulator map
  if (process.env.NODE_ENV !== "production") {
    try {
      // eslint-disable-next-line no-console
      console.debug("defaultFilters.regulator:", defaultFilters?.regulator);
      // eslint-disable-next-line no-console
      console.debug("filtersData.regulator:", filtersData?.regulator);
    } catch {}
  }

  // Normalize backend and legacy regulator names to allow matching across sources
  const normalizeRegName = (s: string) => s?.trim?.();

  const backendRegs = defaultFilters?.regulator;
  let effectiveRegulatorsByCountry: Record<string, string[]> = {};

  // Backend provides a flat array of regulator strings.
  // We accept it as-is and use it directly in the regulator filtering logic.
  if (backendRegs && Array.isArray(backendRegs)) {
    // Flat array from backend - use directly, no transformation needed
    effectiveRegulatorsByCountry = {};
  } else if (backendRegs && typeof backendRegs === "object") {
    // If backend provides a structured object, use it
    effectiveRegulatorsByCountry = backendRegs as Record<string, string[]>;
  } else {
    effectiveRegulatorsByCountry = {};
  }

  const isRomaniaOnly =
    (countryValue.length === 1 && countryValue[0] === "Romania") ||
    (regulatorValue.length === 1 &&
      normalizeRegName(regulatorValue[0]) === normalizeRegName("Romanian Official Gazette"));

  const [countrySortAsc, setCountrySortAsc] = useState(true);
  const [regulatorSortAsc, setRegulatorSortAsc] = useState(true);
  const [classSortAsc, setClassSortAsc] = useState(true);

  useEffect(() => {
    setTagValue(filters.tags);
    setKeywordValue(filters.keywords);
    setCountryValue(filters.country);
    setRegulatorValue(filters.regulator);
    setClassValue(filters.classes);
    setTechProductValue(filters.tech_product);
    setTechSectionValue(filters.tech_section_vat_act);
    setGazetteNumberValue(filters.gazette_number);
    setTaxAlertValue(filters.tax_alert);
    setJudgesValue(filters.judges);
    setStatusValue(filters.status);
    setCityValue(filters.city);
    setSubjectMatterValue(filters.subject_matter);
    setPublicationDateValue([filters.date_from, filters.date_to]);
    setIssueDateValue([filters.date_issue_from, filters.date_issue_to]);
  }, [filters]);

  const clearAllFilters = () => {
    setTagValue([]);
    setKeywordValue([]);
    setCountryValue([]);
    setRegulatorValue([]);
    setClassValue([]);
    setTechProductValue([]);
    setTechSectionValue([]);
    setGazetteNumberValue([]);
    setTaxAlertValue([]);
    setJudgesValue([]);
    setStatusValue([]);
    setCityValue([]);
    setSubjectMatterValue([]);
    setPublicationDateValue([null, null]);
    setIssueDateValue([null, null]);
    onFiltersChange({
      ...filters,
      keywords: [],
      tags: [],
      country: [],
      regulator: [],
      classes: [],
      date_from: null,
      date_to: null,
      date_issue_from: null,
      date_issue_to: null,
      tech_product: [],
      tech_section_vat_act: [],
      gazette_number: [],
      judges: [],
      status: [],
      city: [],
      subject_matter: [],
      search: "",
    });
    collapseAllFilters();
  };

  const collapseAllFilters = () => {
    setIsCountryOpen(false);
    setIsRegulatorOpen(false);
    setIsPublicationDateOpen(false);
    setIsCountryOpen(false);
    setIsRegulatorOpen(false);
    setIsPublicationDateOpen(false);
    setIsIssueDateOpen(false);
    setIsClassOpen(false);
    setIsKeywordOpen(false);
    setIsTechProductOpen(false);
    setIsTechSectionOpen(false);
    setIsTaxAlertOpen(false);
    setIsGazetteNumberOpen(false);
    setIsJudgesOpen(false);
    setIsStatusOpen(false);
    setIsCityOpen(false);
    setIsSubjectMatterOpen(false);
  };

  const handleDateChange = (
    dates: [Date | null, Date | null],
    isIssueDate = false,
    triggeredByClear = false,
  ) => {
    if (triggeredByClear || (dates[0] === null && dates[1] === null)) {
      if (isIssueDate) {
        setIssueDateValue([null, null]);
        onFiltersChange({
          ...filters,
          date_issue_from: null,
          date_issue_to: null,
        });
      } else {
        setPublicationDateValue([null, null]);
        onFiltersChange({
          ...filters,
          date_from: null,
          date_to: null,
        });
      }
      return;
    }

    if (!dates[0] || (dates[1] === null && dates[0] !== null)) {
      if (isIssueDate) setIssueDateValue([dates[0], null]);
      else setPublicationDateValue([dates[0], null]);
      return;
    }

    if (dates[0] && dates[1] && dates[0].getTime() === dates[1].getTime()) {
      if (isIssueDate) {
        setIssueDateValue([dates[0], dates[1]]);
        onFiltersChange({
          ...filters,
          date_issue_from: dates[0],
          date_issue_to: dates[1],
        });
      } else {
        setPublicationDateValue([dates[0], dates[1]]);
        onFiltersChange({
          ...filters,
          date_from: dates[0],
          date_to: dates[1],
        });
      }
      return;
    }

    if (dates[0] && dates[1]) {
      if (isIssueDate) {
        setIssueDateValue(dates);
        onFiltersChange({
          ...filters,
          date_issue_from: dates[0],
          date_issue_to: dates[1],
        });
      } else {
        setPublicationDateValue(dates);
        onFiltersChange({
          ...filters,
          date_from: dates[0],
          date_to: dates[1],
        });
      }
    }
  };

  const handleFilterFieldChange = (
    filterType: keyof IFiltersProps["filters"],
    value: string,
    selectedOptions: MultiValue<{ value: string; label: string }> = [],
  ) => {
    let updatedFilters = { ...filters };

    switch (filterType) {
      case "country":
        const updatedCountry = countryValue.includes(value)
          ? countryValue.filter((c) => c !== value)
          : [...countryValue, value];
        setCountryValue(updatedCountry);
        updatedFilters = { ...updatedFilters, country: updatedCountry };
        break;

      case "regulator":
        let updatedRegulator = regulatorValue.includes(value)
          ? regulatorValue.filter((r) => r !== value)
          : [...regulatorValue, value];
        let updatedClass = classValue;

        if (updatedRegulator.length === 0) {
          updatedClass = [];
          setIsClassOpen(true);
          setIsIssueDateOpen(true);
          setClassValue(updatedClass);
        }

        setRegulatorValue(updatedRegulator);
        updatedFilters = {
          ...updatedFilters,
          regulator: updatedRegulator,
          classes: updatedClass,
        };
        break;

      case "classes":
        const updatedClassValue = classValue.includes(value)
          ? classValue.filter((c) => c !== value)
          : [...classValue, value];
        setClassValue(updatedClassValue);
        updatedFilters = { ...updatedFilters, classes: updatedClassValue };
        break;

      case "keywords":
        const updatedKeywords = selectedOptions.map(
          (option: any) => option.value,
        );
        setKeywordValue(updatedKeywords);
        updatedFilters = { ...updatedFilters, keywords: updatedKeywords };
        break;

      case "tags":
        const updatedTags = selectedOptions.map((option: any) => option.value);
        setTagValue(updatedTags);
        updatedFilters = { ...updatedFilters, tags: updatedTags };
        break;

      case "tech_product":
        const updatedTechProduct = selectedOptions.map(
          (option) => option.value,
        );
        setTechProductValue(updatedTechProduct);
        updatedFilters = {
          ...updatedFilters,
          tech_product: updatedTechProduct,
        };
        break;

      case "tech_section_vat_act":
        const updatedTechSection = selectedOptions.map(
          (option) => option.value,
        );
        setTechSectionValue(updatedTechSection);
        updatedFilters = {
          ...updatedFilters,
          tech_section_vat_act: updatedTechSection,
        };
        break;

      case "tax_alert":
        const updatedTaxAlert = selectedOptions.map((option) => option.value);
        setTaxAlertValue(updatedTaxAlert);
        updatedFilters = {
          ...updatedFilters,
          tax_alert: updatedTaxAlert,
        };
        break;

      case "gazette_number":
        const updatedGazetteNumber = selectedOptions.map(
          (option) => option.value,
        );
        setGazetteNumberValue(updatedGazetteNumber);
        updatedFilters = {
          ...updatedFilters,
          gazette_number: updatedGazetteNumber,
        };
        break;

      case "judges":
        const updatedJudges = selectedOptions.map((option) => option.value);
        setJudgesValue(updatedJudges);
        updatedFilters = { ...updatedFilters, judges: updatedJudges };
        break;

      case "status":
        const updatedStatus = selectedOptions.map((option) => option.value);
        setStatusValue(updatedStatus);
        updatedFilters = { ...updatedFilters, status: updatedStatus };
        break;

      case "city":
        const updatedCity = selectedOptions.map((option) => option.value);
        setCityValue(updatedCity);
        updatedFilters = { ...updatedFilters, city: updatedCity };
        break;

      case "subject_matter":
        const updatedSubjectMatter = selectedOptions.map(
          (option) => option.value,
        );
        setSubjectMatterValue(updatedSubjectMatter);
        updatedFilters = {
          ...updatedFilters,
          subject_matter: updatedSubjectMatter,
        };
        break;

      default:
        break;
    }

    onFiltersChange(updatedFilters);
  };

  const handlePublicationCalendarClose = (
    dates: [Date | null, Date | null],
  ) => {
    if (!dates[0] && !dates[1]) return;

    onFiltersChange({
      ...filters,
      date_from: dates[0],
      date_to: dates[1],
    });
  };

  const handleIssueCalendarClose = (dates: [Date | null, Date | null]) => {
    if (!dates[0] && !dates[1]) return;
    onFiltersChange({
      ...filters,
      date_issue_from: dates[0],
      date_issue_to: dates[1],
    });
  };

  const hasActiveFilters =
    search !== "" ||
    tagValue.length > 0 ||
    keywordValue.length > 0 ||
    countryValue.length > 0 ||
    regulatorValue.length > 0 ||
    classValue.length > 0 ||
    (techProductValue && techProductValue?.length > 0) ||
    (techSectionValue && techSectionValue?.length > 0) ||
    (gazetteNumberValue && gazetteNumberValue?.length > 0) ||
    (judgesValue && judgesValue?.length > 0) ||
    (statusValue && statusValue?.length > 0) ||
    (cityValue && cityValue?.length > 0) ||
    (subjectMatterValue && subjectMatterValue?.length > 0) ||
    publicationDateValue[0] !== null ||
    publicationDateValue[1] !== null ||
    issueDateValue[0] !== null ||
    issueDateValue[1] !== null;

  const isRegionalCourtSelected = regulatorValue.includes(
    "Regional Courts of the Czech Republic",
  );

  const sortArray = (arr: string[], asc: boolean) =>
    [...arr].sort((a, b) => (asc ? a.localeCompare(b) : b.localeCompare(a)));

  // No frontend normalization: use backend country strings as-is.

  const filteredCountries =
    settingsFilter.country.length > 0
      ? (filtersData?.country || []).filter((c) =>
          settingsFilter.country.includes(c),
        )
      : filtersData?.country || [];

  const baseRegulators = filtersData?.regulator || [];

  const backendIsFlat =
    Array.isArray(defaultFilters?.regulator) &&
    defaultFilters?.regulator.length > 0 &&
    typeof defaultFilters.regulator[0] === "string";

  const countryFilterSource =
    settingsFilter.country.length > 0 ? settingsFilter.country : countryValue;

  // Use country strings from settings/url directly (no normalization).
  const selectedCountryKeys = (countryFilterSource || [])
    .filter(Boolean)
    .map((c: string) => (c || "").toString().trim());

  // Derive regulators from returned items when backend provides a flat regulator array.
  const regulatorsFromItems = (() => {
    const src = items || [];
    if (!src || src.length === 0) return [] as string[];
    const set = new Set<string>();
    const filterByCountry = selectedCountryKeys.length > 0;
    for (const it of src) {
      if (!it) continue;
      if (filterByCountry) {
        const itemCountry = (it.country || "").toString().trim();
        if (!selectedCountryKeys.includes(itemCountry)) continue;
      }
      if (it.regulator) set.add(it.regulator);
    }
    return Array.from(set);
  })();

  const allowedByCountry =
    selectedCountryKeys.length > 0
      ? new Set(
          selectedCountryKeys.flatMap((key: string) => {
            const direct = effectiveRegulatorsByCountry[key];
            const sanitizedKey = (key || "").toString().toLowerCase().replace(/\s+/g, "");
            const bySanitized = effectiveRegulatorsByCountry[sanitizedKey];
            return direct || bySanitized || [];
          }),
        )
      : null;

  let regulatorsByCountry: string[] = [];
  if (allowedByCountry && allowedByCountry.size > 0) {
    if (backendIsFlat && regulatorsFromItems.length > 0) {
      regulatorsByCountry = Array.from(new Set([...regulatorsFromItems, ...settingsFilter.regulator]));
    } else {
      regulatorsByCountry = Array.from(new Set([...Array.from(allowedByCountry), ...settingsFilter.regulator]));
    }
  } else {
    regulatorsByCountry = baseRegulators;
  }

  const filteredRegulators =
    settingsFilter.regulator.length > 0
      ? regulatorsByCountry.filter((r) => settingsFilter.regulator.includes(r))
      : regulatorsByCountry;

  const filteredClasses =
    settingsFilter.classes.length > 0
      ? (filtersData?.classes || []).filter((c) =>
          settingsFilter.classes.includes(c),
        )
      : filtersData?.classes || [];

  const sortedCountries = sortArray(filteredCountries, countrySortAsc);
  const sortedRegulators = sortArray(filteredRegulators, regulatorSortAsc);
  const sortedClasses = sortArray(filteredClasses, classSortAsc);

  useEffect(() => {
    if (sortedRegulators.length === 0) return;
    const filtered = regulatorValue.filter((r) => sortedRegulators.includes(r));
    if (filtered.length !== regulatorValue.length) {
      setRegulatorValue(filtered);
      onFiltersChange({ ...filters, regulator: filtered });
    }
  }, [sortedRegulators, regulatorValue, filters, onFiltersChange]);

  useOnClickOutside(filtersRef, () => {
    collapseAllFilters();
  });

  useEffect(() => {
    const scrollContainer =
      filtersRef.current?.querySelector(".overflow-y-scroll");
    if (!scrollContainer) return;

    let timeoutId: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const calendars = document.querySelectorAll(
          ".react-datepicker, .react-datepicker-popper",
        );
        if (calendars.length > 0) {
          calendars.forEach((cal) => {
            const parent = cal.parentElement;
            if (parent) parent.removeChild(cal);
          });
          const activeEl = document.activeElement as HTMLElement | null;
          if (activeEl) activeEl.blur();
        }
      }, 100);
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div
      ref={filtersRef}
      className="h-full flex flex-col bg-base-color border border-slate-300 dark:border-slate-700 rounded-lg overflow-hidden"
    >
      {/* Sticky header */}
      <div className="sticky top-0 bg-base-color">
        <div className="flex justify-between items-center border-b-2 border-slate-200 pt-4 pb-2 px-4 dark:border-slate-600">
          <h3 className="font-semibold text-lg dark:text-gray-300">
            {t("filtersSection.filters")}
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer dark:text-gray-300"
            >
              {t("filtersSection.clearFilters")}
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-scroll p-4 pt-2">
        <div className="mb-2 border-b border-slate-200 dark:border-slate-600">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold dark:text-gray-300 flex items-center">
              {t("filtersSection.country")}
              {!!countryValue.length && (
                <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                  {countryValue.length}
                </span>
              )}
              <button
                type="button"
                className="ml-1 flex items-center p-2 rounded-full dark:hover:bg-gray-700 hover:bg-gray-200 tooltip-sort-country"
                onClick={() => setCountrySortAsc((asc) => !asc)}
              >
                {countrySortAsc ? (
                  <FaSortAlphaDown className="text-del-green focus:outline-none" />
                ) : (
                  <FaSortAlphaUp className="text-del-green focus:outline-none" />
                )}
              </button>
            </h4>
            <button
              onClick={() => setIsCountryOpen(!isCountryOpen)}
              className={`text-sm transform transition-transform duration-300 ${
                isCountryOpen ? "rotate-180" : "rotate-0"
              } hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer`}
            >
              <FiChevronDown size={20} className="dark:text-gray-400" />
            </button>
          </div>
          <div
            className={`${
              isCountryOpen ? "max-h-auto pb-2" : "max-h-0"
            } filter-transition`}
          >
            {sortedCountries?.map((country) => (
              <label
                key={country}
                className="flex text-sm items-start mb-2 text-base-color hover-btn-del-green"
              >
                <input
                  type="checkbox"
                  checked={countryValue.includes(country)}
                  onChange={() => handleFilterFieldChange("country", country)}
                  className="mr-1 mt-0.5 ml-3 accent-del-light-theme-green dark:accent-del-green"
                />
                <span className="leading-tight">{country}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="mb-2 border-b border-slate-200 dark:border-slate-600">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold dark:text-gray-300 flex items-center">
              {t("filtersSection.regulatorName")}{" "}
              {!!regulatorValue.length && (
                <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                  {regulatorValue.length}
                </span>
              )}
              <button
                type="button"
                className="ml-1 flex items-center p-2 rounded-full dark:hover:bg-gray-700 hover:bg-gray-200 tooltip-sort-regulator"
                onClick={() => setRegulatorSortAsc((asc) => !asc)}
              >
                {regulatorSortAsc ? (
                  <FaSortAlphaDown className="text-del-green focus:outline-none" />
                ) : (
                  <FaSortAlphaUp className="text-del-green focus:outline-none" />
                )}
              </button>
            </h4>
            <button
              onClick={() => setIsRegulatorOpen(!isRegulatorOpen)}
              className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
            >
              {isRegulatorOpen ? (
                <FiChevronUp size={20} className="dark:text-gray-400" />
              ) : (
                <FiChevronDown size={20} className="dark:text-gray-400" />
              )}
            </button>
          </div>
          <div
            className={`${
              isRegulatorOpen ? "max-h-auto pb-2" : "max-h-0"
            } filter-transition`}
          >
            <div>
              {sortedRegulators?.map((regulator) => (
                <label
                  key={regulator}
                  className="flex text-sm items-start mb-2 text-base-color dark:hover:text-del-green hover:text-del-light-theme-green"
                >
                  <input
                    type="checkbox"
                    checked={regulatorValue.includes(regulator)}
                    onChange={() =>
                      handleFilterFieldChange("regulator", regulator)
                    }
                    className="mr-1 mt-0.5 ml-3 accent-del-light-theme-green dark:accent-del-green"
                  />
                  <span className="leading-tight">{regulator}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="mb-2 border-b border-slate-200 dark:border-slate-600">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold mb-2 dark:text-gray-300">
              {regulatorValue.some((regulator) =>
                regulator.toLowerCase().includes("calendar"),
              )
                ? `${t("filtersSection.dateOfPublication")}${t("filtersSection.dateOfHearing")}`
                : t("filtersSection.dateOfPublication")}
              {(publicationDateValue[0] !== null ||
                publicationDateValue[1] !== null) && (
                <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                  {publicationDateValue.filter(Boolean).length}
                </span>
              )}
            </h4>
            <button
              onClick={() => setIsPublicationDateOpen(!isPublicationDateOpen)}
              className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
            >
              {isPublicationDateOpen ? (
                <FiChevronUp size={20} className="dark:text-gray-400" />
              ) : (
                <FiChevronDown size={20} className="dark:text-gray-400" />
              )}
            </button>
          </div>
          <div
            className={`${
              isPublicationDateOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
            } filter-transition`}
          >
            <div className="p-2">
              <CustomDatePicker
                startDate={publicationDateValue[0]}
                endDate={publicationDateValue[1]}
                onChange={(date) => handleDateChange(date)}
                onClear={() => handleDateChange([null, null], false, true)}
                onCalendarClose={handlePublicationCalendarClose}
                isRange={true}
              />
            </div>
          </div>
        </div>
        <div className="mb-2 border-b dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold mb-2 dark:text-gray-300">
              {t("filtersSection.keywordsAndTags")}
              {(!!keywordValue?.length || !!tagValue?.length) && (
                <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                  {keywordValue?.length + tagValue?.length}
                </span>
              )}
            </h4>
            <button
              onClick={() => setIsKeywordOpen(!isKeywordOpen)}
              className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
            >
              {isKeywordOpen ? (
                <FiChevronUp size={20} className="dark:text-gray-400" />
              ) : (
                <FiChevronDown size={20} className="dark:text-gray-400" />
              )}
            </button>
          </div>
          <div
            className={`${
              isKeywordOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
            } filter-transition`}
          >
            <div className="p-2 space-y-3">
              <SearchMultiSelect
                data={keywordsData}
                values={keywordValue}
                fieldName="keywords"
                placeholderText={t("filtersSection.searchAndSelectKeywords")}
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("keywords", "", selectedOptions)
                }
              />
              <SearchMultiSelect
                data={tagsData}
                values={tagValue}
                fieldName="tags"
                placeholderText={t("filtersSection.searchAndSelectTags")}
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("tags", "", selectedOptions)
                }
              />
            </div>
          </div>
        </div>
        {isRegionalCourtSelected && (
          <div className="mb-2 border-b border-slate-200 dark:border-slate-600">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300">
                {t("filtersSection.dateOfIssue")}
                {(issueDateValue[0] !== null || issueDateValue[1] !== null) && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {issueDateValue.filter(Boolean).length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsIssueDateOpen(!isIssueDateOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isIssueDateOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isIssueDateOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <div>
                <CustomDatePicker
                  startDate={issueDateValue[0]}
                  endDate={issueDateValue[1]}
                  onChange={(date) => handleDateChange(date, true)}
                  onClear={() => handleDateChange([null, null], false, true)}
                  onCalendarClose={handleIssueCalendarClose}
                  isRange={true}
                />
              </div>
            </div>
          </div>
        )}

        {regulatorValue.length > 0 && filtersData?.tech_product && (
          <div className="mb-2 border-b border-slate-200 dark:border-slate-600">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300 flex items-center">
                {t("filtersSection.techProduct")}
                {!!techProductValue?.length && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {techProductValue.length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsTechProductOpen(!isTechProductOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isTechProductOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isTechProductOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <SearchMultiSelect
                data={techProductData}
                values={techProductValue}
                placeholderText={t("filtersSection.searchAndSelectTechProduct")}
                fieldName="tech_product"
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("tech_product", "", selectedOptions)
                }
              />
            </div>
          </div>
        )}

        {regulatorValue.length > 0 && filtersData?.tech_section_vat_act && (
          <div className="mb-2 border-b dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300 flex items-center">
                {t("filtersSection.techSectionVATAct")}
                {!!techSectionValue?.length && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {techSectionValue.length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsTechSectionOpen(!isTechSectionOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isTechSectionOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isTechSectionOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <SearchMultiSelect
                data={techSectionData}
                values={techSectionValue}
                placeholderText={t("filtersSection.searchAndSelectTechSection")}
                fieldName="tech_section_vat_act"
                onChange={(selectedOptions) =>
                  handleFilterFieldChange(
                    "tech_section_vat_act",
                    "",
                    selectedOptions,
                  )
                }
              />
            </div>
          </div>
        )}
        {isRomaniaOnly && filtersData?.tax_alert && (
          <div className="mb-2 border-b dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300 flex items-center">
                {t("docDetails.taxAlert")}
                {!!taxAlertValue?.length && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {taxAlertValue.length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsTaxAlertOpen(!isTaxAlertOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isTaxAlertOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isTaxAlertOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <SearchMultiSelect
                data={taxAlertData}
                values={taxAlertValue}
                placeholderText={t("filtersSection.searchAndSelectTaxAlert")}
                fieldName="tax_alert"
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("tax_alert", "", selectedOptions)
                }
              />
            </div>
          </div>
        )}
        {regulatorValue.length > 0 && filtersData?.gazette_number && (
          <div className="mb-2 border-b dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300 flex items-center">
                {t("filtersSection.gazetteNumber")}
                {!!gazetteNumberValue?.length && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {gazetteNumberValue.length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsGazetteNumberOpen(!isGazetteNumberOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isGazetteNumberOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isGazetteNumberOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <SearchMultiSelect
                data={gazetteNumberData}
                values={gazetteNumberValue}
                placeholderText={t(
                  "filtersSection.searchAndSelectGazetteNumber",
                )}
                fieldName="gazette_number"
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("gazette_number", "", selectedOptions)
                }
              />
            </div>
          </div>
        )}

        {regulatorValue.length > 0 && filtersData?.judges && (
          <div className="mb-2 border-b dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300 flex items-center">
                {t("filtersSection.judges")}
                {!!judgesValue?.length && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {judgesValue.length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsJudgesOpen(!isJudgesOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isJudgesOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isJudgesOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <SearchMultiSelect
                data={judgesData}
                values={judgesValue}
                placeholderText={t("filtersSection.searchAndSelectJudges")}
                fieldName="judges"
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("judges", "", selectedOptions)
                }
              />
            </div>
          </div>
        )}

        {regulatorValue.length > 0 && filtersData?.status && (
          <div className="mb-2 border-b dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300 flex items-center">
                {t("filtersSection.status")}
                {!!statusValue?.length && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {statusValue.length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isStatusOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isStatusOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <SearchMultiSelect
                data={statusData}
                values={statusValue}
                placeholderText={t("filtersSection.searchAndSelectStatus")}
                fieldName="status"
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("status", "", selectedOptions)
                }
              />
            </div>
          </div>
        )}

        {regulatorValue.length > 0 && filtersData?.city && (
          <div className="mb-2 border-b dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300 flex items-center">
                {t("filtersSection.city")}
                {!!cityValue?.length && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {cityValue.length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsCityOpen(!isCityOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isCityOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isCityOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <SearchMultiSelect
                data={cityData}
                values={cityValue}
                placeholderText={t("filtersSection.searchAndSelectCity")}
                fieldName="city"
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("city", "", selectedOptions)
                }
              />
            </div>
          </div>
        )}

        {regulatorValue.length > 0 && filtersData?.subject_matter && (
          <div className="mb-2 border-b dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold mb-2 dark:text-gray-300 flex items-center">
                {t("filtersSection.subjectMatter")}
                {!!subjectMatterValue?.length && (
                  <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                    {subjectMatterValue.length}
                  </span>
                )}
              </h4>
              <button
                onClick={() => setIsSubjectMatterOpen(!isSubjectMatterOpen)}
                className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                {isSubjectMatterOpen ? (
                  <FiChevronUp size={20} className="dark:text-gray-400" />
                ) : (
                  <FiChevronDown size={20} className="dark:text-gray-400" />
                )}
              </button>
            </div>
            <div
              className={`${
                isSubjectMatterOpen ? "max-h-auto no-overflow pb-2" : "max-h-0"
              } filter-transition`}
            >
              <SearchMultiSelect
                data={subjectMatterData}
                values={subjectMatterValue}
                placeholderText={t(
                  "filtersSection.searchAndSelectSubjectMatter",
                )}
                fieldName="subject_matter"
                onChange={(selectedOptions) =>
                  handleFilterFieldChange("subject_matter", "", selectedOptions)
                }
              />
            </div>
          </div>
        )}

        {(regulatorValue.length > 0 || countryValue.length > 0) && (
          <>
            {sortedClasses.length > 0 && (
              <div>
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold dark:text-gray-300 flex items-center">
                    {t("filtersSection.class")}
                    {!!classValue.length && (
                      <span className="ml-1 bg-del-counter-gray text-black text-xs px-2 py-0.5 rounded">
                        {classValue.length}
                      </span>
                    )}
                    <button
                      type="button"
                      className="ml-1 flex items-center p-2 rounded-full dark:hover:bg-gray-700 hover:bg-gray-200 tooltip-sort-class"
                      onClick={() => setClassSortAsc((asc) => !asc)}
                    >
                      {classSortAsc ? (
                        <FaSortAlphaDown className="text-del-green focus:outline-none" />
                      ) : (
                        <FaSortAlphaUp className="text-del-green focus:outline-none" />
                      )}
                    </button>
                  </h4>
                  <button
                    onClick={() => setIsClassOpen(!isClassOpen)}
                    className="text-sm hover:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
                  >
                    {isClassOpen ? (
                      <FiChevronUp size={20} className="dark:text-gray-400" />
                    ) : (
                      <FiChevronDown size={20} className="dark:text-gray-400" />
                    )}
                  </button>
                </div>
                <div
                  className={`${
                    isClassOpen ? "max-h-auto pb-2" : "max-h-0"
                  } filter-transition text-sm`}
                >
                  <div>
                    {sortedClasses?.map((classItem) => (
                      <label
                        key={classItem}
                        className="flex items-start mb-2 text-base-color hover-btn-del-green"
                      >
                        <input
                          type="checkbox"
                          checked={classValue.includes(
                            classItem.replace(/'/g, ""),
                          )}
                          onChange={() =>
                            handleFilterFieldChange(
                              "classes",
                              classItem.replace(/'/g, ""),
                            )
                          }
                          className="mr-1 mt-0.5 ml-3 accent-del-light-theme-green dark:accent-del-green"
                        />
                        <span className="leading-tight">
                          {classItem.replace(/'/g, "")}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Tooltip
        anchorSelect=".tooltip-sort-country"
        place="top"
        className="text-xs font-light z-50"
        content={
          countrySortAsc ? t("adminPanel.sortAz") : t("adminPanel.sortZa")
        }
      />
      <Tooltip
        anchorSelect=".tooltip-sort-regulator"
        place="top"
        className="text-xs font-light z-50"
        content={
          regulatorSortAsc ? t("adminPanel.sortAz") : t("adminPanel.sortZa")
        }
      />
      <Tooltip
        anchorSelect=".tooltip-sort-class"
        place="top"
        className="text-xs font-light z-50"
        content={classSortAsc ? t("adminPanel.sortAz") : t("adminPanel.sortZa")}
      />
    </div>
  );
};

export { Filters };
